import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, RefreshCw, ExternalLink, Rocket, Sliders, Server, HardDrive, Activity, Boxes } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { WebsiteStatusBadge, HealthBadge, SslBadge } from "./WebsiteBadges";

function fmt(iso) { if (!iso) return "—"; try { return new Date(iso).toLocaleString("id-ID"); } catch { return iso; } }
function Row({ label, value, mono = false }) {
  return (
    <div className="flex items-baseline justify-between gap-3 py-1 border-b border-white/5 last:border-0">
      <div className="text-[10px] text-white/50 uppercase tracking-wider">{label}</div>
      <div className={`text-xs text-white text-right min-w-0 truncate ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}
function Section({ title, icon: Icon, testid, children }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        {Icon ? <Icon size={14} className="text-emerald-300" /> : null}
        <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">{title}</div>
      </div>
      {children}
    </div>
  );
}

export default function WebsiteDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [w, setW] = useState(null);
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w1, h1] = await Promise.all([
        api.get(`/platform/websites/${id}`),
        api.get(`/platform/websites/${id}/history`),
      ]);
      setW(w1.data);
      setHistory(h1.data);
    } catch (e) { notifyError(e); setW(null); setHistory(null); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setBusy(true);
    try { await api.post(`/platform/websites/${id}/refresh`, {}); toast.success("Refreshed."); load(); }
    catch (e) { notifyError(e); } finally { setBusy(false); }
  };

  if (loading) {
    return <PlatformLayout title="Website Detail"><div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center" data-testid="wsd-loading">Memuat…</div></PlatformLayout>;
  }
  if (!w) {
    return (
      <PlatformLayout title="Website Detail">
        <div className="p-8 rounded-2xl border border-white/10 text-center space-y-3" data-testid="wsd-not-found">
          <div className="text-white/70 text-sm">Website tidak ditemukan atau sudah dihapus.</div>
          <Button variant="ghost" onClick={() => nav("/platform/websites")} className="text-white/80 hover:text-white hover:bg-white/10" data-testid="wsd-not-found-back">
            <ArrowLeft size={14} className="mr-1" /> Kembali ke daftar
          </Button>
        </div>
      </PlatformLayout>
    );
  }

  return (
    <PlatformLayout title={w.tenant_name}>
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <Button variant="ghost" onClick={() => nav("/platform/websites")} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="wsd-back">
          <ArrowLeft size={14} className="mr-1" /> Kembali
        </Button>
        <div className="ml-auto flex items-center gap-2 flex-wrap">
          <Button variant="ghost" onClick={refresh} disabled={busy} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="wsd-refresh">
            <RefreshCw size={14} className={`mr-1 ${busy ? "animate-spin" : ""}`} /> Refresh
          </Button>
          {w.domain && (
            <a href={`https://${w.domain}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-bold" data-testid="wsd-open-website">
              <ExternalLink size={12} /> Open Website
            </a>
          )}
          {w.deployment_id && (
            <Link to={`/platform/deployments/${w.deployment_id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-xs font-bold" data-testid="wsd-open-deployment">
              <Rocket size={12} /> Deployment
            </Link>
          )}
          {w.last_infra_execution_id && (
            <Link to={`/platform/infrastructure/${w.last_infra_execution_id}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-indigo-500/40 text-indigo-300 hover:bg-indigo-500/10 text-xs font-bold" data-testid="wsd-open-infra">
              <Server size={12} /> Infrastructure
            </Link>
          )}
        </div>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="text-white font-display font-black text-lg" data-testid="wsd-tenant-name">{w.tenant_name}</div>
        <WebsiteStatusBadge status={w.website_status} />
        <HealthBadge status={w.health_status} />
        <SslBadge status={w.ssl_status} />
        <span className="text-xs text-white/50 font-mono">· {w.tenant_code}</span>
        <span className="ml-auto text-[10px] text-white/40">Updated {fmt(w.updated_at)}</span>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Section title="Website Information" icon={Rocket} testid="wsd-section-website">
          <Row label="Domain" value={w.domain} mono />
          <Row label="Subdomain" value={w.subdomain} mono />
          <Row label="Environment" value={w.environment} />
          <Row label="Version" value={w.version} mono />
          <Row label="Response Time" value={w.response_time != null ? `${(w.response_time * 1000).toFixed(0)} ms` : "—"} mono />
          <Row label="HTTP Status" value={w.http_status} mono />
          <Row label="Last Deployment" value={fmt(w.last_deployment)} />
        </Section>

        <Section title="Tenant + Subscription" icon={Sliders} testid="wsd-section-tenant">
          <Row label="Tenant Name" value={w.tenant_name} />
          <Row label="Tenant Code" value={w.tenant_code} mono />
          <Row label="Tenant Slug" value={w.tenant_slug} mono />
          <Row label="Tenant Status" value={w.tenant_status} />
          <Row label="Plan" value={w.plan} />
          <Row label="Subscription Status" value={w.subscription_status} />
        </Section>

        <Section title="Infrastructure + Health" icon={Server} testid="wsd-section-infra">
          <Row label="Last Execution" value={w.last_infra_execution_number} mono />
          <Row label="Adapter" value={w.last_infra_adapter} />
          <Row label="Workspace" value={w.workspace_path} mono />
          <Row label="Deployment Status" value={w.deployment_status} />
          <Row label="Health Status" value={w.health_status} />
          <Row label="Last Health Check" value={fmt(w.last_health_check)} />
        </Section>

        <Section title="Containers" icon={Boxes} testid="wsd-section-containers">
          <Row label="Backend" value={w.backend_status} mono />
          <Row label="Frontend" value={w.frontend_status} mono />
          <Row label="Nginx" value={w.nginx_status} mono />
          <Row label="Database" value={w.database_status} mono />
          <Row label="Total Containers" value={w.docker_container_count} />
        </Section>

        <Section title="Domain + SSL" icon={HardDrive} testid="wsd-section-domain">
          <Row label="Domain" value={w.domain} mono />
          <Row label="SSL Status" value={w.ssl_status} />
          <Row label="SSL Expires" value={fmt(w.ssl_expires_at)} />
          <Row label="Database Name" value={w.database_name} mono />
          <Row label="Storage Usage" value={w.storage_usage != null ? `${w.storage_usage} MB` : "—"} />
        </Section>

        <Section title="License" icon={Sliders} testid="wsd-section-license">
          {w.license_panel ? (
            <>
              <Row label="Plan" value={w.license_panel.plan_code} mono />
              <Row label="Status" value={w.license_panel.status} />
              <Row label="License Key" value={w.license_panel.license_key} mono />
              <Row label="Expires" value={fmt(w.license_panel.expires_at)} />
              <Row label="Remaining Website" value={w.license_panel.remaining_website === -1 ? "∞" : w.license_panel.remaining_website} mono />
              <Row label="Remaining Deployment" value={w.license_panel.remaining_deployment === -1 ? "∞" : w.license_panel.remaining_deployment} mono />
              <Row label="Remaining Storage (GB)" value={w.license_panel.remaining_storage_gb === -1 ? "∞" : w.license_panel.remaining_storage_gb} mono />
              <Row label="Remaining Admin" value={w.license_panel.remaining_admin === -1 ? "∞" : w.license_panel.remaining_admin} mono />
            </>
          ) : (
            <div className="text-[10px] text-white/40 italic py-2" data-testid="wsd-license-none">Belum ada license aktif.</div>
          )}
        </Section>

        <Section title="History (Last Executions)" icon={Activity} testid="wsd-section-history">
          {(history?.executions || []).slice(0, 5).length === 0 ? (
            <div className="text-[10px] text-white/40 italic py-2">Belum ada execution history.</div>
          ) : (
            <div className="space-y-1.5">
              {(history?.executions || []).slice(0, 5).map((e) => (
                <div key={e.id} className="rounded-lg border border-white/10 bg-black/30 p-2" data-testid={`wsd-history-${e.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono font-bold text-white truncate">{e.execution_number}</span>
                    <span className="ml-auto text-[10px] font-mono text-white/60">{e.status}</span>
                  </div>
                  <div className="text-[10px] text-white/40 font-mono mt-0.5">{e.adapter} · {fmt(e.finished_at || e.started_at)}</div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>
    </PlatformLayout>
  );
}
